import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AppConfig {
  production: boolean;
  /**
   * Authentication provider configurations
   * Proper separation of provider-specific settings
   */
  authProviders: {
    google?: {
      enabled: boolean;
      authorizationUrl: string;
      clientId?: string;
    };
    devAuth?: {
      enabled: boolean;
      authorizationUrl: string;
    };
  };
  /**
   * Active authentication provider
   */
  activeAuthProvider: 'google' | 'devauth';
  chatApiUrl: string;
  wsEndpoint: string;
  /**
   * URL for refreshing authentication tokens
   */
  tokenRefreshUrl?: string;
  /**
   * URL for logging out the user
   */
  logoutUrl?: string;
  telemetry: {
    enabled: boolean;
    serviceName: string;
    serviceVersion: string;
    environment: string;
    collectorUrl: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: AppConfig | null = null;
  private defaultConfig: Partial<AppConfig> = {
    production: environment.production
  };

  constructor(private http: HttpClient) {}

  /**
   * Playwright-independent logging that writes to localStorage and window object
   * This ensures we have logs even if Playwright's console capture fails
   */
  private debugLog(level: 'log' | 'error', message: string, data?: any) {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, level, message, data };

    // Write to console (captured by Playwright if working)
    if (level === 'error') {
      console.error(message, data);
    } else {
      console.log(message, data);
    }

    // Write to localStorage (survives even if page crashes/reloads)
    try {
      const existingLogs = localStorage.getItem('__debug_logs') || '[]';
      const logs = JSON.parse(existingLogs);
      logs.push(logEntry);
      // Keep last 100 entries to avoid filling storage
      if (logs.length > 100) logs.shift();
      localStorage.setItem('__debug_logs', JSON.stringify(logs));
    } catch (e) {
      // Ignore localStorage errors (private mode, etc.)
    }

    // Write to window object (can be extracted via page.evaluate() or DOM inspection)
    try {
      if (typeof window !== 'undefined') {
        (window as any).__debugLogs = (window as any).__debugLogs || [];
        (window as any).__debugLogs.push(logEntry);
      }
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Loads configuration from the external config file.
   * Should be called during app initialization.
   */
  async loadConfig(): Promise<void> {
    const startTime = performance.now();
    const requestId = Math.random().toString(36).substring(7);

    this.debugLog('log', `[ConfigService] 🔄 Starting config load (ID: ${requestId})`, {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    try {
      // Add a timestamp to prevent caching
      const timestamp = new Date().getTime();
      const configUrl = `/assets/config/config.json?t=${timestamp}`;

      this.debugLog('log', `[ConfigService] 📡 Initiating HTTP GET (ID: ${requestId})`, {
        url: configUrl,
        elapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });

      // Load configuration with 45-second timeout (should complete in < 5s normally)
      const loadedConfig = await firstValueFrom(
        this.http.get<AppConfig>(configUrl).pipe(
          timeout({
            each: 45000, // 45 second timeout
            with: () => {
              const elapsed = performance.now() - startTime;
              this.debugLog('error', `[ConfigService] ⏱️ TIMEOUT after ${elapsed.toFixed(2)}ms (ID: ${requestId})`);
              throw new Error(`Config loading timed out after ${elapsed.toFixed(2)}ms`);
            }
          })
        )
      );

      this.debugLog('log', `[ConfigService] ✅ HTTP response received (ID: ${requestId})`, {
        elapsed: `${(performance.now() - startTime).toFixed(2)}ms`,
        configKeys: Object.keys(loadedConfig)
      });

      // Merge with default config (environment settings take precedence)
      this.config = { ...this.defaultConfig, ...loadedConfig };

      // If WebSocket endpoint is not explicitly set, derive it from the chatApiUrl
      if (!this.config.wsEndpoint && this.config.chatApiUrl) {
        this.config.wsEndpoint = this.derivedWebSocketUrl(this.config.chatApiUrl);
      }

      // If tokenRefreshUrl is not set, derive it from the chatApiUrl
      if (!this.config.tokenRefreshUrl && this.config.chatApiUrl) {
        this.config.tokenRefreshUrl = this.deriveTokenRefreshUrl(this.config.chatApiUrl);
      }

      const totalTime = performance.now() - startTime;
      this.debugLog('log', `[ConfigService] 🎉 Configuration loaded successfully (ID: ${requestId})`, {
        totalTime: `${totalTime.toFixed(2)}ms`,
        wsEndpoint: this.config.wsEndpoint,
        chatApiUrl: this.config.chatApiUrl
      });
      this.debugLog('log', '[ConfigService] Auth providers in loaded config:', this.config.authProviders);
    } catch (error: any) {
      const totalTime = performance.now() - startTime;
      this.debugLog('error', `[ConfigService] ❌ Failed to load configuration (ID: ${requestId})`, {
        totalTime: `${totalTime.toFixed(2)}ms`,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack?.split('\n').slice(0, 3).join('\n')
      });

      // Fallback to default config in case of error
      this.config = { ...this.defaultConfig } as AppConfig;
      throw error;
    }
  }

  /**
   * Returns the current application configuration.
   * @throws Error if configuration is not yet loaded
   */
  getConfig(): AppConfig {
    if (!this.config) {
      console.warn('Configuration not loaded, returning fallback configuration');
      return { 
        ...this.defaultConfig, 
        authProviders: {
          devAuth: {
            enabled: true,
            authorizationUrl: '/api/oauth2/authorization/devauth'
          }
        },
        activeAuthProvider: 'devauth',
        chatApiUrl: '',
        wsEndpoint: '',
        tokenRefreshUrl: '',
        logoutUrl: '',
        telemetry: {
          enabled: true,
          serviceName: 'finderwise-ui',
          serviceVersion: '1.0.0',
          environment: 'dev',
          collectorUrl: '/telemetry/v1/traces'
        }
      } as AppConfig;
    }
    return this.config;
  }

  /**
   * Returns the environment from telemetry config
   */
  getEnvironment(): string {
    return this.getConfig().telemetry.environment;
  }

  /**
   * Returns the backend API URL
   */
  getBackendUrl(): string {
    return this.getConfig().chatApiUrl;
  }

  /**
   * Derive WebSocket URL from HTTP URL
   * This creates a sensible default for WebSocket endpoint based on the chat API URL
   */
  private derivedWebSocketUrl(httpUrl: string): string {
    try {
      // Handle relative URLs by constructing an absolute URL based on current location
      let url: URL;
      if (httpUrl.startsWith('http://') || httpUrl.startsWith('https://')) {
        url = new URL(httpUrl);
      } else {
        // For relative URLs, use the current window location as base
        url = new URL(httpUrl, window.location.origin);
      }

      // Change protocol from http/https to ws/wss
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

      // Replace /chat with /ws in the pathname if it exists
      const pathname = url.pathname.replace(/\/chat$/, '/ws');

      // Construct the WebSocket URL
      return `${protocol}//${url.host}${pathname}`;
    } catch (error) {
      console.warn('Could not derive WebSocket URL from chat API URL:', error);

      // Return a default WebSocket URL based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}/api/ws`;
    }
  }

  /**
   * Derive token refresh URL from chat API URL
   * This creates a sensible default for token refresh endpoint
   */
  private deriveTokenRefreshUrl(httpUrl: string): string {
    try {
      // Handle relative URLs by constructing an absolute URL based on current location
      let url: URL;
      if (httpUrl.startsWith('http://') || httpUrl.startsWith('https://')) {
        url = new URL(httpUrl);
      } else {
        // For relative URLs, use the current window location as base
        url = new URL(httpUrl, window.location.origin);
      }

      // Build the token refresh URL using the same host but with /api/auth/refresh path
      const baseUrl = `${url.protocol}//${url.host}`;

      // Check if the URL already has /api prefix
      if (url.pathname.startsWith('/api/')) {
        return `${baseUrl}/api/auth/refresh`;
      } else {
        return `${baseUrl}/auth/refresh`;
      }
    } catch (error) {
      console.warn('Could not derive token refresh URL from chat API URL:', error);

      // Return a default token refresh URL based on current location
      return `${window.location.protocol}//${window.location.host}/api/auth/refresh`;
    }
  }
}
